/**
 * Le matériel transférable : ce que le filtre du planning garde, et ce qu'il ne sait pas encore.
 *
 * *Demande du métier, 18/08/2026 : « je vais aller cocher dans générique les matériels
 * transférables, et toi dans le planning tu me mets un bouton pour filtrer les matériels
 * transférables sur la vue planning ».* Le responsable transport ouvre une contre-étude et veut
 * n'y voir que les machines qui montent sur un porte-engin — ni la main-d'œuvre, ni les
 * fournitures, ni les camions qui se rendent au chantier par leurs propres moyens.
 *
 * ## Deux fonctions, et elles vont ensemble
 *
 * Le prédicat dit ce qui reste ; le compteur dit ce qui manque. Séparer les deux serait la
 * faute : `porteEngin` a **trois** états, et ce que le prédicat écarte faute de réponse au
 * référentiel doit se dire quelque part. Une machine dont le code n'a pas encore été tranché
 * disparaîtrait sinon d'une frise qui a l'air complète, et le porte-engin partirait à moitié
 * chargé. C'est la leçon des absences non rattachées du 16/08 — un écran incomplet se lit
 * exactement comme un écran complet.
 *
 * Ils vivent ici plutôt que dans l'écran pour que le filtre et l'avertissement portent sur le
 * même périmètre : deux définitions finiraient par signaler des codes qui n'expliquent rien de
 * ce qu'on voit.
 */
import type { DepoVue, NoeudVue, OuvrageVue, RessourceVue } from "@planif/moteur";

/**
 * Cette ligne est-elle une machine à mettre sur un porte-engin ?
 *
 * **Faux sur un code non tranché**, délibérément : tant que le référentiel n'a pas répondu, on
 * ne prétend pas savoir. C'est `codesATrancher` qui rend cette ignorance visible.
 */
export function partSurPorteEngin(r: RessourceVue): boolean {
  return r.estMateriel && r.porteEngin === true;
}

/** Les tâches d'un arbre, à plat. Une tâche est une feuille : seul un chapitre a des enfants. */
function tachesDe(noeuds: readonly NoeudVue[]): OuvrageVue[] {
  return noeuds.flatMap((n) => (n.nature === "ouvrage" ? [n] : tachesDe(n.enfants)));
}

/**
 * Les codes matériel de ce chantier qui attendent encore une réponse au référentiel.
 *
 * Triés et dédoublonnés : un même code revient sur autant de lignes que d'ouvrages où il
 * travaille, et le signaler cinq fois ferait passer un oubli pour une avalanche.
 *
 * Les postes écartés du chiffrage n'y entrent pas — leurs machines ne partiront pas, donc
 * réclamer une réponse à leur sujet serait du bruit.
 */
export function codesATrancher(depo: DepoVue): readonly string[] {
  const codes = new Set<string>();
  for (const tache of tachesDe(depo.racines)) {
    if (!tache.retenu) continue;
    for (const r of tache.ressources) {
      if (r.estMateriel && r.porteEngin === null) codes.add(r.code);
    }
  }
  return [...codes].sort();
}
